/**
 * Render session state (implementation-plan.md §2.3, §2.5, §6).
 *
 * Owns the half of the session that lives after the review gate:
 *   - the capability probe result and the renderer it selects (WebCodecs or the server fallback)
 *   - the coach's BrandingKit, resolved into the OverlayConfig every frame is composited with
 *   - the sequential render queue ("Rendering 3 of 12 clips…") over the CONFIRMED clips only
 *   - per-clip download and the batch ZIP
 *   - a RenderJob row in D1 per clip, carrying renderer, render_time_ms and size_bytes
 *
 * The review-first contract holds here too: this store only ever looks at clips the coach
 * confirmed (status `confirmed`), never at `proposed` or `edited` ones.
 */
import { create } from 'zustand';
import {
  createRenderJob,
  getBranding,
  updateRenderJob,
  type BrandingKitRow,
  type RenderJobRow,
  type StructuredClipRow,
} from '../api/client';
import {
  DEFAULT_BRANDING_KIT,
  brandingKitToOverlay,
  type BrandingKit,
} from '../branding';
import { effectiveLabel, effectiveWindow } from '../review';
import {
  buildZipBlob,
  clipFilename,
  downloadBlob,
  runRenderQueue,
  selectRenderer,
  type RenderItemResult,
  type RenderQueueProgress,
  type CapabilityReport,
  type ClipRenderer,
  type AudioTrackMode,
  type RendererKind,
} from '../render';

/** The gate, restated for this screen: only a clip the coach approved is renderable. */
export function confirmedClips(clips: StructuredClipRow[]): StructuredClipRow[] {
  // `rendered` still counts: it is a confirmed clip that has already been through the queue.
  return clips.filter((clip) => clip.status === 'confirmed' || clip.status === 'rendered');
}

export interface RenderRunInput {
  sessionId: string;
  clips: StructuredClipRow[];
  /** Signed media URL of the source footage (the renderer streams from it). */
  sourceVideo: string | Blob;
  quality?: 'preview' | 'export';
  /** `mute` renders video-only; omitted lets the capability report decide (§2.6). */
  audioTrack?: AudioTrackMode;
  token: string | null;
  /** The Clerk user id whose BrandingKit applies. */
  userId?: string | null;
  /**
   * Free-tier watermark (card t_31859fc7 §2). Omitted = the store's own tier, which defaults to
   * WATERMARKED: a caller who forgets to say does not get an unwatermarked export by accident.
   */
  watermark?: boolean;
}

interface RenderState {
  report: CapabilityReport | null;
  rendererKind: RendererKind | null;
  /** The session being exported — carried so the UI (and the ZIP name) can name it. */
  sessionId: string | null;
  branding: BrandingKit | null;
  running: boolean;
  progress: RenderQueueProgress | null;
  items: RenderItemResult[];
  /** Jobs as D1 recorded them, keyed by clip id. */
  jobs: Record<string, RenderJobRow>;
  error: string | null;
  /** Set when the probe found no H.264 encoder AND the server fallback refused (Phase 2). */
  serverUnavailable: string | null;
  /**
   * Whether exports carry "Made with Callout". Driven by the account tier from
   * GET /api/user/usage, never by the coach, and it defaults to TRUE so a render can only lose the
   * watermark by the account actually being on a paid tier.
   */
  watermark: boolean;
  tier: string | null;

  probe: (token?: string | null) => Promise<CapabilityReport>;
  /** Records the account tier that decides the watermark; call it wherever usage is loaded. */
  setSubscriptionTier: (tier: string | null) => void;
  loadBranding: (userId: string, token: string | null) => Promise<void>;
  runAll: (input: RenderRunInput) => Promise<RenderItemResult[]>;
  download: (clipId: string) => void;
  downloadAll: () => Promise<number>;
  reset: () => void;
}

const initial = {
  report: null,
  rendererKind: null,
  sessionId: null,
  branding: null,
  running: false,
  progress: null,
  items: [] as RenderItemResult[],
  jobs: {} as Record<string, RenderJobRow>,
  error: null,
  serverUnavailable: null,
  // Fail closed: until the account tier is known, exports carry the watermark.
  watermark: true,
  tier: null,
};

/** D1's BrandingKit row → the client's BrandingKit (snake_case → camelCase, one place). */
export function brandingKitFromRow(row: BrandingKitRow | null): BrandingKit | null {
  if (!row) return null;
  return {
    id: row.id,
    ownerType: row.owner_type,
    ownerId: row.owner_id,
    logoUrl: row.logo_url ?? undefined,
    primaryColor: row.primary_color as BrandingKit['primaryColor'],
    secondaryColor: row.secondary_color as BrandingKit['secondaryColor'],
    clubName: row.club_name ?? undefined,
  };
}

/** The overlay every clip is composited with: the club's kit, or Callout's own colours. */
export function brandingKitOrDefault(kit: BrandingKit | null): BrandingKit {
  return kit ?? { ...DEFAULT_BRANDING_KIT, id: 'default', ownerId: 'default' };
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

export const useRenderStore = create<RenderState>((set, get) => ({
  ...initial,

  probe: async (token = undefined) => {
    const { renderer, report } = await selectRenderer({ token });
    set({ report, rendererKind: renderer.kind });
    return report;
  },

  setSubscriptionTier: (tier) => set({ tier, watermark: tier !== 'pro' }),

  loadBranding: async (userId, token) => {
    try {
      const { brandingKit } = await getBranding(userId, token);
      set({ branding: brandingKitFromRow(brandingKit) });
    } catch {
      // Branding is decorative: a failure means Callout's own colours, never a blocked render.
      set({ branding: null });
    }
  },

  runAll: async (input) => {
    const { sessionId, clips, sourceVideo, token } = input;
    const quality = input.quality ?? 'export';
    const targets = confirmedClips(clips);
    const branding = get().branding;

    set({
      running: true,
      error: null,
      serverUnavailable: null,
      items: [],
      progress: null,
      sessionId,
    });
    if (targets.length === 0) {
      set({ running: false, error: 'No confirmed clips to render — confirm a clip first.' });
      return [];
    }

    try {
      const { renderer, report } = await selectRenderer({ token });
      set({ report, rendererKind: renderer.kind });

      const items = targets.map((clip) => {
        const { tIn, tOut } = effectiveWindow(clip);
        return { clipId: clip.id, title: effectiveLabel(clip), tIn, tOut };
      });

      // Seed the queue with every clip at `queued` BEFORE the first encode, so the screen shows
      // the work that is still ahead (and the clip being encoded can be marked in flight).
      set({
        items: items.map((item) => ({
          clipId: item.clipId,
          title: item.title,
          status: 'queued' as const,
        })),
      });

      // One RenderJob row per clip, created BEFORE the encode so a crashed render is visible
      // in D1 as `running` rather than vanishing.
      const jobIds = new Map<string, string>();
      for (const item of items) {
        try {
          const { renderJob } = await createRenderJob(
            { clipId: item.clipId, renderer: renderer.kind, quality, status: 'running' },
            token,
          );
          jobIds.set(item.clipId, renderJob.id);
          set((state) => ({ jobs: { ...state.jobs, [item.clipId]: renderJob } }));
        } catch (error) {
          // A missing job row must not stop the render; it only costs the metrics trail.
          console.warn('render job create failed', item.clipId, errorMessage(error));
        }
      }

      const results = await runRenderQueue({
        items,
        renderer: renderer as ClipRenderer,
        overlayFor: (item) =>
          brandingKitToOverlay(brandingKitOrDefault(branding), { title: item.title }, {
            watermark: input.watermark ?? get().watermark,
          }),
        sourceVideo,
        quality,
        audioTrack: input.audioTrack,
        onProgress: (update) => {
          set((state) => ({
            progress: update,
            // Exactly one clip is ever in flight — mark it so the row shows the work.
            items: state.items.map((entry) =>
              entry.clipId === update.clipId
                ? { ...entry, status: 'rendering' as const }
                : entry.status === 'rendering'
                  ? { ...entry, status: 'queued' as const }
                  : entry,
            ),
          }));
        },
        onItemDone: (result) => {
          set((state) => ({
            // Replace in place: the queue's order is the coach's order, and it is what names
            // the downloaded files.
            items: state.items.some((entry) => entry.clipId === result.clipId)
              ? state.items.map((entry) => (entry.clipId === result.clipId ? result : entry))
              : [...state.items, result],
          }));
          const jobId = jobIds.get(result.clipId);
          if (!jobId) return;
          const patch =
            result.status === 'complete' && result.result
              ? {
                  status: 'complete' as const,
                  durationSeconds: result.result.duration,
                  sizeBytes: result.result.sizeBytes,
                  renderTimeMs: result.result.renderTimeMs,
                }
              : { status: 'failed' as const, error: result.error ?? 'render failed' };
          void updateRenderJob(jobId, patch, token)
            .then(({ renderJob }) =>
              set((state) => ({ jobs: { ...state.jobs, [result.clipId]: renderJob } })),
            )
            .catch((error) => console.warn('render job update failed', jobId, errorMessage(error)));
        },
      });

      set({ running: false, progress: null });

      // A session-wide "the server path is not switched on yet" state: when the probe selected
      // the server renderer, every clip fails with the same Phase-2 message. Showing that once,
      // clearly, is the honest presentation — three identical red rows would look like a bug.
      if (renderer.kind === 'server') {
        const failure = results.find((result) => result.status === 'failed')?.error;
        if (failure) set({ serverUnavailable: failure });
      }
      return results;
    } catch (error) {
      const message = errorMessage(error);
      set({ running: false, error: message });
      // The server-fallback refusal is a known Phase 2 state, not a bug — surface it as one.
      if (get().rendererKind === 'server') set({ serverUnavailable: message });
      return [];
    }
  },

  download: (clipId) => {
    const { items } = get();
    const index = items.findIndex((item) => item.clipId === clipId);
    const item = items[index];
    if (!item?.result) return;
    downloadBlob(item.result.blob, clipFilename(index, item.title));
  },

  downloadAll: async () => {
    const { items, sessionId } = get();
    const ready = items.filter((item) => item.result);
    if (ready.length === 0) return 0;
    const entries = ready.map((item) => ({
      name: clipFilename(
        items.findIndex((entry) => entry.clipId === item.clipId),
        item.title,
      ),
      blob: item.result!.blob,
    }));
    const zip = await buildZipBlob(entries);
    const suffix = sessionId ? sessionId.slice(-6).toLowerCase() : String(entries.length);
    downloadBlob(zip, `callout-clips-${suffix}.zip`);
    return ready.length;
  },

  reset: () => set({ ...initial }),
}));
